import { XMarkIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { CollectionVersionRef } from "../../../../shared/references/contentReferences";
import IconButton from "../../../../ui/buttons/IconButton";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import { CorpusSearch, type CorpusSearchHit } from "../../../search";
import HighlightedSnippet from "../../../search/query/HighlightedSnippet";
import { useCorpusSelection } from "../../../sources/corpus/corpusSelectionContext";
import type { ThreadSubject } from "../threadIntent";
import { addEntry, entryReference, MAXIMUM_ENTRIES, selectionError } from "./entrySelection";

const NO_CORPORA = [] as const;

interface DocumentSetFieldProps {
  projectId: string;
  entries: readonly ThreadSubject[];
  /**
   * The one corpus version to search: the one the Thread binds, or the one its chosen entries
   * are in. `null`: the corpora Documents shows, until the first entry is chosen.
   */
  searchIn: CollectionVersionRef | null;
  /** `picked`: the corpus version of an added entry; `null` when one was removed. */
  onChange: (entries: readonly ThreadSubject[], picked: CollectionVersionRef | null) => void;
  disabled: boolean;
  /** What the start still needs here, in words (F101); `null` when nothing. */
  needed: string | null;
}

/**
 * The documents the work is about, as chips, and DMW's one search to add more (F31, F102):
 * the search Documents shows, over the corpora it shows. The first chip is the subject. A
 * Thread binds one corpus version, so once an entry is chosen the search stays in its version.
 */
export default function DocumentSetField({ projectId, entries, searchIn, onChange, disabled, needed }: DocumentSetFieldProps) {
  const { t } = useTranslation();
  const { corpora, hiddenCorpusIds } = useCorpusSelection();
  const [message, setMessage] = useState<string | null>(null);
  // A new search after each pick: the results close once their entry is chosen (TR-A2-07).
  const [searchRound, setSearchRound] = useState(0);

  const add = (hit: CorpusSearchHit) => {
    const candidate: ThreadSubject = { document: hit.document, label: hit.documentKey, passage: hit.snippet };
    const error = selectionError(entries, candidate);
    setMessage(error === null ? null : t(`threads.prepare.documents.${error}`, { count: MAXIMUM_ENTRIES }));
    if (error === null) {
      onChange(addEntry(entries, candidate), hit.corpusVersion);
      setSearchRound((round) => round + 1);
    }
  };

  return (
    <div className="space-y-2">
      {entries.length === 0 ? null : (
        <ul className="space-y-1.5" aria-label={t("threads.prepare.documents.chosen", { count: entries.length })}>
          {entries.map((entry, index) => (
            <li key={entryReference(entry)} className="app-card flex items-start gap-2 p-2">
              <span className="min-w-0 flex-1 space-y-0.5">
                <span className="block text-sm font-semibold">
                  {entry.label}{entry.segmentPath === undefined ? "" : ` #${entry.segmentPath}`}
                  {index === 0 && entries.length > 1 ? <span className="app-text-muted font-normal"> · {t("threads.prepare.documents.subject")}</span> : null}
                </span>
                {entry.passage === undefined ? null : <HighlightedSnippet snippet={entry.passage} />}
              </span>
              <IconButton
                className="p-0.5"
                label={t("threads.prepare.documents.remove", { document: entry.label })}
                disabled={disabled}
                onClick={() => { onChange(entries.filter((_, position) => position !== index), null); }}
              >
                <XMarkIcon className="h-3 w-3" aria-hidden="true" />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      {needed === null ? null : <p className={FORM_HELP_TEXT_CLASS}>{needed}</p>}
      {disabled ? null : (
        <CorpusSearch
          key={searchRound}
          projectId={projectId}
          corpora={{ list: corpora?.status === "loaded" ? corpora.value : NO_CORPORA, excluded: hiddenCorpusIds }}
          version={searchIn ?? undefined}
          hitAction={{ kind: "pick", onPick: add }}
        />
      )}
      {message === null ? null : <p role="alert" className="text-sm">{message}</p>}
    </div>
  );
}
